import cloneDeep from 'clone-deep'
import type { InputType } from 'config'
import { useEffect, useState } from 'react'
import { Modal } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { relaxUnsetOptionalFields } from './optionalWhenUnset'
import type { IAccountingVendor } from './types'

export const defaultInputs = (categories: Record<number, string>): Record<string, InputType> => {
  return {
    name: {
      inputType: 'text',
      title: 'Name',
      required: true,
      span: 3,
    },
    address: {
      inputType: 'Map',
      title: 'Address',
      required: true,
      span: 3,
    },
    ein: {
      inputType: 'text',
      type: 'entityTaxID',
      title: 'Tax ID / EIN',
      required: true,
      span: 3,
    },
    email: {
      inputType: 'text',
      type: 'email',
      // Optional: a vendor with no email simply gets no deposit notice when they are paid by ACH.
      title: 'Email',
      span: 3,
    },
    type: {
      inputType: 'select',
      title: 'Vendor Type',
      options: categories,
      hasDefaultOption: true,
      sort: true,
      sortKey: 'value',
      required: true,
      span: 3,
    },
    isActive: {
      inputType: 'toggle',
      title: 'Active',
      // A deactivated vendor stays in the admin list but is hidden from payee selection.
      value: true,
      span: 3,
    },
    sectionTax: {
      inputType: 'section',
      title: '1099 Reporting',
      span: 'full',
    },
    taxFormType: {
      inputType: 'select',
      title: '1099 Form',
      options: {
        '1099-NEC': '1099-NEC (services, contractor work)',
        '1099-MISC': '1099-MISC (rent, other income)',
        none: 'Not reportable',
      },
      hasDefaultOption: true,
      defaultOptionText: 'Not classified yet',
      // Deliberately not defaulted and not required. A vendor left blank is reported nowhere, and
      // the Tax Filing review area counts those as an exception by name rather than letting the
      // year go out quietly short. Guessing a default here would hide exactly that.
      span: 3,
    },
    taxBoxCode: {
      inputType: 'text',
      title: 'Box',
      // The box number on the chosen form. Blank takes the form's usual box (1 on a NEC).
      span: 3,
    },
    taxExemptReason: {
      inputType: 'text',
      title: 'Why not reportable',
      // Asked only for a vendor marked not reportable, because somebody has to be able to defend
      // the omission a year later when the IRS asks.
      span: 3,
    },
    section1: {
      inputType: 'section',
      title: 'Payment Information',
      span: 'full',
    },
    isWire: {
      inputType: 'checkbox',
      title: 'Wire',
      span: 2,
    },
    isACH: {
      inputType: 'checkbox',
      title: 'ACH',
      span: 2,
    },
    isCheck: {
      inputType: 'checkbox',
      title: 'Check',
      span: 2,
    },
    wireGroup: {
      inputType: 'group',
      title: 'Wire Information',
      span: 'full',
    },
    wireBankAddress: {
      inputType: 'map',
      title: 'Bank Address',
      span: 3,
    },
    wireAccountHolder: {
      inputType: 'text',
      title: 'Account Holder Name',
      span: 3,
    },
    wireRoutingNo: {
      inputType: 'text',
      type: 'routingNo',
      title: 'Routing Number',
      span: 3,
    },
    wireAccountNo: {
      inputType: 'text',
      type: 'bankAccountNo',
      title: 'Account Number',
      span: 3,
    },
    achGroup: {
      inputType: 'group',
      title: 'ACH Information',
      span: 'full',
    },
    achBankAddress: {
      inputType: 'map',
      title: 'Bank Address',
      span: 3,
    },
    achAccountHolder: {
      inputType: 'text',
      title: 'Account Holder Name',
      span: 3,
    },
    achRoutingNo: {
      inputType: 'text',
      type: 'routingNo',
      title: 'Routing Number',
      span: 3,
    },
    achAccountNo: {
      inputType: 'text',
      type: 'bankAccountNo',
      title: 'Account Number',
      span: 3,
    },
    note: {
      inputType: 'textarea',
      type: 'text',
      title: 'Note',
      value: '',
      span: 'full',
      required: false,
    },
    files: {
      inputType: 'FileTable',
      title: 'File',
      required: false,
      acceptFileTypes: '.pdf',
      filePath: `loan/accounting/vendors`,
      span: 'full',
      showCategory: false,
      showStatus: false,
    },
  }
}

export const AddVendor = ({
  index,
  item,
  categories,
  initialName,
  childLevel = 2,
  onClose,
  onUpdate,
}: {
  index: number
  item: IAccountingVendor | null
  categories: Record<number, string>
  initialName?: string
  childLevel?: number
  onClose: Function
  onUpdate: Function
}) => {
  const [loading, setLoading] = useState(false)
  const [inputs, setInputs] = useState(defaultInputs(categories))

  useEffect(() => {
    if (item || initialName) {
      const newInputs = cloneDeep(inputs)
      if (item) {
        Object.keys(inputs).forEach((key) => {
          if (key == 'date' && (item as any)[key]) (item as any)[key] = (item as any)[key].substring(0, 10)
          newInputs[key].value = (item as any)[key]
        })
        // A record that arrived without a Vendor Type or an EIN must not become un-editable
        // because of it: see the rule.
        relaxUnsetOptionalFields(newInputs)
      } else if (initialName) {
        newInputs.name.value = initialName
      }
      setInputs(newInputs)
    }
  }, [])

  useEffect(() => {
    setInputs((prev) => {
      const newInputs = cloneDeep(prev)
      newInputs.type.options = categories
      return newInputs
    })
  }, [categories])

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
  }

  const onSubmit = async () => {
    let hasError = false

    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in inputs) {
      newInputs[key].error = InputValidate(newInputs[key])
      data[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    setInputs(newInputs)
    if (hasError) return

    setLoading(true)
    onUpdate(data)
  }

  return (
    <Modal
      title={item ? `Update Vendor - No.${index + 1}` : 'Add Vendor'}
      titleOkay="Submit"
      loading={loading}
      onClose={() => onClose()}
      onOk={onSubmit}
      childLevel={childLevel}
      isOpen
    >
      <div className="text-gray-900">
        <div className="w-144 grid gap-4 md:grid-cols-6 grid-cols-1 mb-3 mt-2">
          {Object.keys(inputs).map((key, index) => {
            if ((key.includes('wire') && !inputs.isWire.value) || (key.includes('ach') && !inputs.isACH.value)) return
            // The box and the exemption reason are each asked of exactly one kind of vendor: a box
            // for a reportable one, a reason for an exempt one, and neither until the form type
            // has been chosen.
            const taxFormType = inputs.taxFormType?.value
            if (key === 'taxBoxCode' && (!taxFormType || taxFormType === 'none')) return
            if (key === 'taxExemptReason' && taxFormType !== 'none') return
            let input = inputs[key]
            return (
              <div className={`input md:col-span-${input.span || 1}`} key={index}>
                <RenderInput input={input} Key={key} onChange={onChange} />
              </div>
            )
          })}
        </div>
      </div>
    </Modal>
  )
}
